/**
 * Custom Report Builder Component
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import {
  Build as BuildIcon,
  Download as DownloadIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface ReportConfig {
  report_name: string;
  report_type: string;
  filters: Record<string, any>;
  columns: string[];
}

const REPORT_TYPES = [
  { value: 'inventory_status', label: 'Inventory Status' },
  { value: 'sales_analysis', label: 'Sales Analysis' },
  { value: 'supplier_performance', label: 'Supplier Performance' },
  { value: 'replenishment_orders', label: 'Replenishment Orders' },
];

const COLUMN_OPTIONS: Record<string, string[]> = {
  inventory_status: ['product_name', 'sku', 'store_name', 'current_stock', 'reorder_point', 'unit_cost', 'category'],
  sales_analysis: ['transaction_date', 'product_name', 'store_name', 'quantity_sold', 'unit_price', 'revenue'],
  supplier_performance: ['supplier_name', 'total_orders', 'on_time_deliveries', 'performance_score'],
  replenishment_orders: ['order_date', 'product_name', 'store_name', 'quantity_ordered', 'status', 'expected_delivery_date'],
};

const CustomReportBuilder: React.FC = () => {
  const [config, setConfig] = useState<ReportConfig>({
    report_name: '',
    report_type: '',
    filters: {},
    columns: [],
  });

  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReportTypeChange = (event: SelectChangeEvent) => {
    const reportType = event.target.value;
    setConfig({
      ...config,
      report_type: reportType,
      columns: COLUMN_OPTIONS[reportType] || [],
      filters: {},
    });
    setReportData(null);
  };

  const handleColumnsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setConfig({
      ...config,
      columns: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleFilterChange = (filterName: string, value: any) => {
    setConfig({
      ...config,
      filters: {
        ...config.filters,
        [filterName]: value,
      },
    });
  };

  const handleGenerateReport = async () => {
    if (!config.report_name || !config.report_type || config.columns.length === 0) {
      setError('Please fill in report name, type, and select at least one column');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.generateCustomReport(config);
      setReportData(response.data);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Handle Pydantic validation errors
        const errorMessages = detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        setError(errorMessages || 'Validation error');
      } else {
        setError(detail || 'Failed to generate report');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async (format: string) => {
    if (!reportData) {
      setError('Please generate the report first');
      return;
    }

    try {
      const response = await api.exportCustomReport(config, format);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.report_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to export report');
    }
  };

  const renderFilters = () => {
    if (!config.report_type) return null;

    switch (config.report_type) {
      case 'inventory_status':
        return (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Store ID"
                value={config.filters.store_id || ''}
                onChange={(e) => handleFilterChange('store_id', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Category"
                value={config.filters.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Min Stock"
                value={config.filters.min_stock || ''}
                onChange={(e) => handleFilterChange('min_stock', parseInt(e.target.value))}
              />
            </Grid>
          </>
        );

      case 'sales_analysis':
        return (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={config.filters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={config.filters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Store ID"
                value={config.filters.store_id || ''}
                onChange={(e) => handleFilterChange('store_id', e.target.value)}
              />
            </Grid>
          </>
        );

      case 'replenishment_orders':
        return (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={config.filters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={config.filters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={config.filters.status || ''}
                  label="Order Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="ORDERED">Ordered</MenuItem>
                  <MenuItem value="DELIVERED">Delivered</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BuildIcon sx={{ fontSize: 40, mr: 2 }} />
        <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>Custom Report Builder</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {/* Report Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Report Name"
              value={config.report_name}
              onChange={(e) => setConfig({ ...config, report_name: e.target.value })}
              placeholder="e.g., Monthly Inventory Review"
            />
          </Grid>

          {/* Report Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={config.report_type}
                label="Report Type"
                onChange={handleReportTypeChange}
              >
                {REPORT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Columns */}
          {config.report_type && (
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Columns to Include</InputLabel>
                <Select
                  multiple
                  value={config.columns}
                  onChange={handleColumnsChange}
                  input={<OutlinedInput label="Columns to Include" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {COLUMN_OPTIONS[config.report_type]?.map((column) => (
                    <MenuItem key={column} value={column}>
                      {column}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Filters */}
          {renderFilters()}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <RunIcon />}
                onClick={handleGenerateReport}
                disabled={loading}
              >
                Generate Report
              </Button>
              {reportData && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExportReport('csv')}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExportReport('json')}
                  >
                    Export JSON
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Report Results */}
      {reportData && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Report Results
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Generated: {reportData.generated_at} | Rows: {reportData.row_count}
          </Typography>

          <TableContainer sx={{ mt: 2, maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {config.columns.map((column) => (
                    <TableCell key={column}>
                      <strong>{column}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.data.slice(0, 100).map((row: any, index: number) => (
                  <TableRow key={index} hover>
                    {config.columns.map((column) => (
                      <TableCell key={column}>{row[column]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {reportData.row_count > 100 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Showing first 100 rows of {reportData.row_count} total rows. Export to see all data.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CustomReportBuilder;
